import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import PinPad from '@/components/desktop/PinPad';
import { getElectronAPI } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { useAuthStore } from '@/store/authStore';
import type { LocalCashierPublicProfile } from '@/desktop/types';
import { verifyDesktopCloudSessionAfterUnlock } from '@/desktop/cloudSession';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SwitchCashierModal({ open, onClose }: Props) {
  const api = getElectronAPI();
  const { setAuth } = useAuthStore();
  const applyUnlock = useDesktopSessionStore((s) => s.applyUnlock);
  const [profiles, setProfiles] = useState<LocalCashierPublicProfile[]>([]);
  const [selected, setSelected] = useState<LocalCashierPublicProfile | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !api) return;
    void api.vault.listProfiles().then(setProfiles);
  }, [open, api]);

  useEffect(() => {
    if (!selected || pin.length < (selected.pinLength || 4)) return;
    void (async () => {
      if (!api) return;
      setBusy(true);
      setError('');
      try {
        const result = await api.vault.unlockWithPin(selected.localId, pin);
        const session = applyUnlock(result);
        if (session) {
          if (session.company?.code) localStorage.setItem('company_code', session.company.code);
          setAuth(session.user, session.company, session.role, session.canUsePOS);
          void verifyDesktopCloudSessionAfterUnlock({
            cloudApplied: Boolean(result.cloudApplied),
            serverUserId: result.profile?.serverUserId,
          });
        }
        setPin('');
        onClose();
      } catch (err) {
        setError((err as Error)?.message || 'Switch failed');
        setPin('');
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, selected?.localId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Switch cashier</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {profiles.map((p) => (
            <button
              key={p.localId}
              type="button"
              onClick={() => {
                setSelected(p);
                setPin('');
                setError('');
              }}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${
                selected?.localId === p.localId ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
              }`}
            >
              {p.displayName}
              <span className="text-xs text-gray-500 block">{p.role}</span>
            </button>
          ))}
          {selected && (
            <>
              <PinPad value={pin} onChange={setPin} maxLength={selected.pinLength || 4} disabled={busy} />
              {busy && (
                <div className="flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              )}
              {error && <p className="text-sm text-red-600 text-center">{error}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
